import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { usePageMeta } from '../hooks/usePageMeta';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, User, Building2, CheckCircle2 } from 'lucide-react';

export const ServicesHubPage = () => {
  const { t } = useLanguage();
  usePageMeta('servicesHub.title', 'seo.servicesDescription');

  const audiences = [
    {
      id: 'private',
      icon: User,
      title: t('servicesHub.privateTitle'),
      tagline: t('servicesHub.privateTagline'),
      description: t('servicesHub.privateDesc'),
      bullets: [
        t('servicesHub.privateBullet1'),
        t('servicesHub.privateBullet2'),
        t('servicesHub.privateBullet3'),
        t('servicesHub.privateBullet4'),
      ],
      cta: t('servicesHub.privateCta'),
      href: '/services/private',
    },
    {
      id: 'companies',
      icon: Building2,
      title: t('servicesHub.companiesTitle'),
      tagline: t('servicesHub.companiesTagline'),
      description: t('servicesHub.companiesDesc'),
      bullets: [
        t('servicesHub.companiesBullet1'),
        t('servicesHub.companiesBullet2'),
        t('servicesHub.companiesBullet3'),
        t('servicesHub.companiesBullet4'),
      ],
      cta: t('servicesHub.companiesCta'),
      href: '/services/companies',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="services-hub-page">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-sea-blue/5 via-white to-bronze/5 py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('servicesHub.title')}
            </h1>
            <p className="text-lg text-gray-700 leading-relaxed">
              {t('servicesHub.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Audience cards */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {audiences.map((a) => {
              const Icon = a.icon;
              return (
                <Card
                  key={a.id}
                  className="group flex flex-col border-2 hover:border-sea-blue/40 hover:shadow-xl transition-all duration-300"
                  data-testid={`audience-card-${a.id}`}
                >
                  <CardContent className="p-8 flex-1 flex flex-col">
                    <div className="w-16 h-16 rounded-lg bg-sea-blue/10 flex items-center justify-center mb-6 group-hover:bg-sea-blue group-hover:scale-110 transition-all duration-300">
                      <Icon className="h-8 w-8 text-sea-blue group-hover:text-white transition-colors" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                      {a.title}
                    </h2>
                    <p className="text-bronze font-semibold mb-4">{a.tagline}</p>
                    <p className="text-gray-700 leading-relaxed mb-6">
                      {a.description}
                    </p>
                    <ul className="space-y-3 mb-8 flex-1">
                      {a.bullets.map((b, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-700">
                          <CheckCircle2 className="h-5 w-5 text-sea-blue flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{b}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to={a.href} data-testid={`audience-cta-${a.id}`}>
                      <Button
                        size="lg"
                        className="w-full bg-sea-blue hover:bg-sea-blue/90 text-white"
                      >
                        {a.cta}
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-sea-blue text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t('servicesHub.ctaTitle')}
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            {t('servicesHub.ctaSubtitle')}
          </p>
          <Link to="/booking" data-testid="hub-booking-cta">
            <Button size="lg" className="bg-bronze hover:bg-bronze/90 text-white">
              {t('servicesHub.ctaButton')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
